import { LineChart, Line, ResponsiveContainer, AreaChart, Area } from "recharts";
import { fmtPctPlain } from "@/lib/format";

// Tone -> color mapping for sector-specific KPIs
const TONE_COLORS = {
    primary: { text: "#0A2A22", bg: "#0A2A22", light: "#0A2A2215", accent: "#0A2A22" },
    signal: { text: "#1F5F5B", bg: "#1F5F5B", light: "#1F5F5B15", accent: "#1F5F5B" },
    success: { text: "#2D6A4F", bg: "#2D6A4F", light: "#2D6A4F15", accent: "#2D6A4F" },
    teal: { text: "#3B7A73", bg: "#3B7A73", light: "#3B7A7315", accent: "#3B7A73" },
    plum: { text: "#7B3F5C", bg: "#7B3F5C", light: "#7B3F5C15", accent: "#7B3F5C" },
    warn: { text: "#D97706", bg: "#D97706", light: "#D9770615", accent: "#D97706" },
    danger: { text: "#B23A48", bg: "#B23A48", light: "#B23A4815", accent: "#B23A48" },
    saffron: { text: "#E19825", bg: "#E19825", light: "#E1982515", accent: "#E19825" },
};

const QUARTILE_BADGES = {
    top: { label: "TOP QUARTILE", cls: "bg-fp-success text-white" },
    above_median: { label: "ABOVE MEDIAN", cls: "bg-fp-teal text-white" },
    below_median: { label: "BELOW MEDIAN", cls: "bg-fp-terracotta text-white" },
};

export default function SectorKPIs({ kpis }) {
    if (!kpis || kpis.length === 0) return null;
    return (
        <div className="bg-white border border-fp-border p-6" data-testid="sector-specific-kpis">
            <div className="flex items-baseline justify-between mb-4">
                <div>
                    <div className="font-sans text-[11px] uppercase tracking-[0.25em] text-fp-muted mb-1">Sector-Specific KPIs · Benchmarked</div>
                    <h3 className="font-serif text-2xl text-fp-ink">Industry Performance Indicators</h3>
                </div>
                <div className="font-mono text-[10px] text-fp-muted uppercase tracking-widest">Source · McKinsey MPI · Deloitte · IEA · BCG</div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {kpis.map((k) => <SectorKpiCard key={k.code} kpi={k} />)}
            </div>
        </div>
    );
}

const SectorKpiCard = ({ kpi }) => {
    const tone = TONE_COLORS[kpi.tone] || TONE_COLORS.primary;
    const badge = QUARTILE_BADGES[kpi.quartile] || QUARTILE_BADGES.above_median;
    const data = kpi.trend.map((v, i) => ({ i, v }));

    // Progress toward p75
    const range = Math.abs(kpi.benchmark_p75 - kpi.benchmark_p50) || 1;
    const pctPos = kpi.direction === "up"
        ? Math.min(100, Math.max(0, ((kpi.value - kpi.benchmark_p50 * 0.75) / (kpi.benchmark_p75 * 1.15 - kpi.benchmark_p50 * 0.75)) * 100))
        : Math.min(100, Math.max(0, 100 - ((kpi.value - kpi.benchmark_p75 * 0.85) / (kpi.benchmark_p50 * 1.2 - kpi.benchmark_p75 * 0.85)) * 100));

    return (
        <div className="relative p-4 border-l-4 group hover:shadow-sm transition-shadow" style={{ borderLeftColor: tone.accent, backgroundColor: tone.light }} data-testid={`skpi-${kpi.code}`}>
            <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                    <div className="font-mono text-[9px] uppercase tracking-widest" style={{ color: tone.text, opacity: 0.7 }}>{kpi.code}</div>
                    <div className="font-sans text-xs text-fp-ink font-medium leading-tight mt-0.5">{kpi.label}</div>
                </div>
                <span className={`shrink-0 px-1.5 py-0.5 font-sans text-[9px] tracking-wider ${badge.cls}`}>{badge.label}</span>
            </div>

            <div className="flex items-baseline gap-1.5 mt-3">
                <div className="font-mono text-2xl font-medium" style={{ color: tone.text }}>{typeof kpi.value === "number" ? kpi.value.toLocaleString("en-US", { maximumFractionDigits: 2 }) : kpi.value}</div>
                <div className="font-mono text-xs text-fp-muted">{kpi.unit}</div>
            </div>

            {/* Sparkline */}
            <div className="h-6 -mx-1 mt-2 opacity-80 group-hover:opacity-100 transition-opacity">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data}>
                        <Area type="monotone" dataKey="v" stroke={tone.accent} strokeWidth={1.5} fill={tone.accent} fillOpacity={0.12} isAnimationActive={false} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            {/* Benchmark bar */}
            <div className="mt-3">
                <div className="flex items-center justify-between font-mono text-[9px] text-fp-muted uppercase tracking-wider mb-1">
                    <span>P50 · {kpi.benchmark_p50}{kpi.unit === "%" ? "%" : ""}</span>
                    <span>P75 · {kpi.benchmark_p75}{kpi.unit === "%" ? "%" : ""}</span>
                </div>
                <div className="relative h-1 bg-fp-border">
                    <div className="absolute inset-y-0 left-0" style={{ width: `${pctPos}%`, backgroundColor: tone.accent }} />
                    <div className="absolute inset-y-0 w-px bg-fp-ink/40" style={{ left: "40%" }} />
                    <div className="absolute inset-y-0 w-px bg-fp-ink/40" style={{ left: "75%" }} />
                </div>
            </div>
        </div>
    );
};
